import React from "react";
import { keyframes } from "@emotion/react";
import { Box, SvgIconProps, Typography } from "@mui/material";
import { CheckCircle, Autorenew, Schedule, Warning } from "@mui/icons-material";
import { translate } from "../../../../translate/translate";
import { SapImportDisplayStatus } from "../../../../models/general/sap-integration-health";

const MOCK_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

interface StatusStyle {
  background: string;
  border: string;
  color: string;
  labelKey: string;
  Icon: React.ComponentType<SvgIconProps>;
}

const STATUS_STYLES: Record<SapImportDisplayStatus, StatusStyle> = {
  healthy: { background: "#ecfdf5", border: "#a7f3d0", color: "#047857", labelKey: "import_status_healthy", Icon: CheckCircle },
  critical: { background: "#fef2f2", border: "#fecaca", color: "#b91c1c", labelKey: "import_status_critical", Icon: Warning },
  processing: { background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8", labelKey: "import_status_processing", Icon: Autorenew },
  not_run: { background: "#f9fafb", border: "#e5e7eb", color: "#6b7280", labelKey: "import_status_not_run", Icon: Schedule },
};

interface IntegrationStatusBadgeProps {
  status: SapImportDisplayStatus;
  compact?: boolean;
}

const IntegrationStatusBadgeComponent = ({ status, compact = false }: IntegrationStatusBadgeProps) => {
  const style = STATUS_STYLES[status];
  const { Icon } = style;
  const iconSize = compact ? 11 : 13;

  return (
    <Box
      sx={{
        alignItems: "center",
        backgroundColor: style.background,
        border: `1px solid ${style.border}`,
        borderRadius: "9999px",
        display: "inline-flex",
        flexShrink: 0,
        gap: "4px",
        padding: compact ? "2px 8px" : "4px 10px",
        width: "auto",
      }}
    >
      <Icon
        sx={{
          color: style.color,
          fontSize: iconSize,
          animation: status === "processing" ? `${spin} 1s linear infinite` : "none",
        }}
      />
      <Typography sx={{ fontFamily: MOCK_FONT_FAMILY, fontSize: compact ? "10px" : "12px", fontWeight: 600, color: style.color }}>
        {translate.t(style.labelKey)}
      </Typography>
    </Box>
  );
};

export default IntegrationStatusBadgeComponent;
